import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import JsBarcode from "jsbarcode";
import { Printer, Search, X, Grid3x3, Plus } from "lucide-react";
import api, { inr } from "../lib/api";

// Build the per-size variant barcode value. The product SKU may already carry a
// trailing size token (new format AMB-{serial}-{SIZE}); strip it before appending
// the actual variant size so we never get e.g. "AMB-7-L-L". "Free Size" -> "FS".
const SIZE_TOKEN = /-(XS|S|M|L|XL|XXL|FS)$/i;
const sizeCode = (s) =>
  String(s || "").trim().toLowerCase().replace(/\s/g, "") === "freesize"
    ? "FS"
    : String(s || "").toUpperCase();
const variantCode = (sku, size) => {
  const base = String(sku || "").replace(SIZE_TOKEN, "");
  return size ? `${base}-${sizeCode(size)}` : base;
};

// One printable label. The barcode is rasterised to a HIGH-RESOLUTION canvas
// with INTEGER bar widths (no fractional/sub-pixel bars) so thermal printers
// (pure black/white, 203dpi) print crisp bars with no anti-alias smear. The
// canvas is then displayed at a fixed mm width and the browser is told not to
// smooth it on print (image-rendering: pixelated + print-color-adjust: exact).
function BarcodeLabel({ product, code, size, label }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && code) {
      try {
        JsBarcode(ref.current, code, {
          format: "CODE128",
          displayValue: true,
          font: "monospace",
          fontOptions: "bold",
          fontSize: 30,        // large — the canvas is high-res, then scaled down
          width: 3,            // INTEGER module width in canvas px (crisp bars)
          height: 150,         // high-res height for sharp bars
          margin: 0,
          textMargin: 4,
        });
      } catch (e) { /* invalid chars */ }
    }
  }, [code]);
  return (
    <div className="bc-label border border-black p-0.5 flex flex-col items-center justify-center break-inside-avoid overflow-hidden box-border" style={{ width: "50mm", height: "25mm" }} data-testid="barcode-label">
      <div className="bc-inner">
        <p className="text-[12px] font-bold text-center leading-tight truncate w-full" style={{ fontFamily: "sans-serif" }}>{product.name}</p>
        {label ? <p className="text-[12px] font-bold leading-tight" style={{ fontFamily: "sans-serif" }}>{label}</p>
               : (size && <p className="text-[12px] font-bold leading-tight" style={{ fontFamily: "sans-serif" }}>Size: {size}</p>)}
        <canvas ref={ref} className="bc-canvas" style={{ width: "46mm", height: "auto", maxHeight: "12mm" }}></canvas>
        <p className="text-[14px] font-bold font-mono leading-tight">Rs {Number(product.price || 0).toLocaleString("en-IN")}</p>
      </div>
    </div>
  );
}

// A sheet entry: { product, variants: [{size, copies}] }
export default function PosBarcodes() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [entries, setEntries] = useState([]);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [browseList, setBrowseList] = useState([]);
  const [browseQuery, setBrowseQuery] = useState("");
  const [browseLoaded, setBrowseLoaded] = useState(false);

  const openBrowse = async () => {
    setBrowseOpen(true);
    if (!browseLoaded) {
      try {
        const { data } = await api.get("/admin/products");
        setBrowseList(Array.isArray(data) ? data : (data.items || []));
        setBrowseLoaded(true);
      } catch { /* ignore */ }
    }
  };

  const filteredBrowse = useMemo(() => {
    const q = browseQuery.trim().toLowerCase();
    if (!q) return browseList;
    return browseList.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.sku || "").toLowerCase().includes(q) ||
      (p.category || "").toLowerCase().includes(q)
    );
  }, [browseList, browseQuery]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get("/pos/product-search", { params: { q: query } }).then((r) => setResults(r.data));
    }, 220);
    return () => clearTimeout(t);
  }, [query]);

  const addProduct = (p) => {
    setEntries((prev) => {
      if (prev.find((e) => e.product.id === p.id)) return prev;
      const cv = p.color_variants || [];
      if (cv.length) {
        // One barcode per colour, using that colour's own SKU.
        return [...prev, { product: p, variants: cv.map((v) => ({ size: v.color, label: v.color, code: v.sku, copies: 1, selected: true })) }];
      }
      const sizes = (p.sizes && p.sizes.length) ? p.sizes : [""];
      return [...prev, { product: p, variants: sizes.map((s) => ({ size: s, copies: 1, selected: true })) }];
    });
    setQuery(""); setResults([]);
  };

  // Deep-link from the Stock tab: /pos/barcodes?sku=AMB-8-FS auto-adds that product.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const sku = searchParams.get("sku");
    if (!sku) return;
    api.get("/pos/product-search", { params: { q: sku } })
      .then((r) => { if (r.data && r.data[0]) addProduct(r.data[0]); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setVariantCopies = (ei, vi, v) =>
    setEntries((prev) => prev.map((e, i) => i === ei
      ? { ...e, variants: e.variants.map((x, j) => j === vi ? { ...x, copies: Math.max(0, Number(v) || 0) } : x) }
      : e
    ));

  const toggleVariant = (ei, vi) =>
    setEntries((prev) => prev.map((e, i) => i === ei
      ? { ...e, variants: e.variants.map((x, j) => j === vi ? { ...x, selected: !x.selected } : x) }
      : e
    ));

  const setAllSelected = (ei, sel) => setEntries((prev) => prev.map((e, i) => i === ei
    ? { ...e, variants: e.variants.map((x) => ({ ...x, selected: sel })) }
    : e
  ));

  const removeEntry = (ei) => setEntries((prev) => prev.filter((_, i) => i !== ei));
  const setAll = (ei, v) => setEntries((prev) => prev.map((e, i) => i === ei
    ? { ...e, variants: e.variants.map((x) => ({ ...x, copies: Math.max(0, Number(v) || 0) })) }
    : e
  ));

  // Flatten entries to a print list, keeping order — only ticked variants print.
  const printLabels = entries.flatMap(({ product, variants }) =>
    variants.flatMap(({ size, label, code, copies, selected }) => {
      if (!selected) return [];
      const finalCode = code || variantCode(product.sku, size);
      return Array.from({ length: copies }).map(() => ({ product, code: finalCode, size, label }));
    })
  );
  const totalLabels = printLabels.length;

  return (
    <div className="p-8" data-testid="pos-barcodes-page">
      <style>{`
        .bc-canvas {
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
        }
        .bc-inner { width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; }
        @media print {
          html, body, #root { height: auto !important; margin: 0 !important; padding: 0 !important; background: #fff !important; }
          /* Keep thermal barcodes crisp: pure black/white, no smoothing, no scaling blur */
          .bc-canvas {
            image-rendering: pixelated !important;
            image-rendering: crisp-edges !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .pos-print-sheet-wrapper, .pos-print-sheet-wrapper * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          /* Hide all on-screen controls */
          .pos-print-hide { display: none !important; }
          /* Collapse the POS chrome so it adds no height / blank pages */
          [data-testid="pos-layout"] { display: block !important; min-height: 0 !important; height: auto !important; }
          [data-testid="pos-layout"] > aside { display: none !important; }
          [data-testid="pos-layout"] > header { display: none !important; }
          [data-testid="pos-layout"] > main { display: block !important; min-height: 0 !important; overflow: visible !important; }
          [data-testid="pos-barcodes-page"] { padding: 0 !important; }
          /* Hide toast/overlay portals that live outside #root */
          [data-sonner-toaster] { display: none !important; }
          /* The label sheet flows normally — no absolute positioning */
          .pos-print-sheet-wrapper { position: static !important; padding: 0 !important; margin: 0 !important; box-shadow: none !important; background: #fff !important; }
          .pos-print-sheet-wrapper > div { display: block !important; gap: 0 !important; }
          /* One 50mm x 25mm label per page, horizontal (left-to-right). */
          .bc-label {
            width: 50mm !important;
            height: 25mm !important;
            box-sizing: border-box !important;
            margin: 0 !important;
            padding: 0.5mm !important;
            border: 0 !important;
            overflow: hidden !important;
            break-inside: avoid !important;
            page-break-inside: avoid !important;
            break-after: page !important;
            page-break-after: always !important;
          }
          .bc-label:last-child { break-after: auto !important; page-break-after: auto !important; }
          @page { size: 50mm 25mm; margin: 0; }
        }
      `}</style>

      <div className="pos-print-hide">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-slate-100">Barcode Labels</h1>
            <p className="text-sm text-slate-500">Each size variant gets its own barcode (e.g. <span className="font-mono text-amber-400">AMB-1005-M</span>). Scanning it at the till auto-picks that size.</p>
          </div>
          <button disabled={totalLabels === 0} onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded font-medium text-sm disabled:opacity-40" data-testid="barcode-print">
            <Printer size={14} /> Print sheet · {totalLabels} labels
          </button>
        </div>

        <div className="flex items-center gap-3 mb-4 max-w-xl">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by SKU or name to add to sheet" className="w-full bg-slate-900 border border-slate-800 rounded pl-10 pr-4 py-3 text-sm text-slate-200" data-testid="barcode-search" />
          </div>
          <button onClick={openBrowse} className="flex items-center gap-2 px-4 py-3 border border-slate-800 rounded text-sm text-slate-300 hover:text-amber-400 hover:border-amber-500 transition-colors" data-testid="barcode-browse" title="Browse the full catalogue">
            <Grid3x3 size={15} /> Browse
          </button>
        </div>
        {results.length > 0 && (
          <div className="bg-slate-900 border border-slate-800 rounded max-w-xl mb-6 divide-y divide-slate-800">
            {results.map((p) => (
              <button key={p.id} onClick={() => addProduct(p)} className="w-full text-left flex items-center gap-3 p-3 hover:bg-slate-800">
                <img src={p.primary_image} alt="" className="h-10 w-10 object-cover rounded" />
                <div className="flex-1">
                  <p className="text-sm text-slate-100">{p.name}</p>
                  <p className="text-xs text-slate-500 font-mono">SKU {p.sku || "—"} · Sizes: {(p.sizes || []).join(", ") || "—"}</p>
                </div>
                <span className="text-xs text-amber-400">+ Add</span>
              </button>
            ))}
          </div>
        )}

        <div className="bg-slate-900 border border-slate-800 rounded p-4 mb-6">
          <h3 className="text-xs uppercase tracking-widest text-slate-400 mb-3">Sheet ({totalLabels} labels)</h3>
          {entries.length === 0 ? (
            <p className="text-center py-6 text-slate-600 text-sm">Add products above. Each product fans out into one row per size variant.</p>
          ) : (
            <div className="space-y-3">
              {entries.map((e, ei) => (
                <div key={ei} className="p-3 bg-slate-950 border border-slate-800 rounded">
                  <div className="flex items-start gap-3">
                    <img src={e.product.primary_image} alt="" className="h-11 w-11 object-cover rounded" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-100">{e.product.name}</p>
                      <p className="text-xs text-slate-500 font-mono">SKU {e.product.sku || "—"}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setAllSelected(ei, true)} className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-amber-400 border border-slate-800 rounded px-2 py-1" data-testid={`barcode-select-all-${e.product.sku}`}>All</button>
                      <button onClick={() => setAllSelected(ei, false)} className="text-[10px] uppercase tracking-widest text-slate-400 hover:text-amber-400 border border-slate-800 rounded px-2 py-1" data-testid={`barcode-select-none-${e.product.sku}`}>None</button>
                      <label className="text-[10px] text-slate-500 uppercase tracking-widest ml-1">Set all</label>
                      <input type="number" min={0} defaultValue={1} onChange={(ev) => setAll(ei, ev.target.value)} className="w-14 bg-slate-900 border border-slate-800 rounded px-2 py-1 text-xs text-slate-200 font-mono text-right" />
                      <button onClick={() => removeEntry(ei)} className="text-slate-500 hover:text-red-400 p-1"><X size={13} /></button>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 pl-14">
                    {e.variants.map((v, vi) => (
                      <div key={vi} className={`flex items-center gap-2 bg-slate-900 border rounded px-2 py-1.5 transition-opacity ${v.selected ? "border-slate-800" : "border-slate-800/60 opacity-45"}`}>
                        <input
                          type="checkbox"
                          checked={!!v.selected}
                          onChange={() => toggleVariant(ei, vi)}
                          className="h-3.5 w-3.5 accent-amber-500 cursor-pointer"
                          title={v.selected ? "Will print — untick to skip" : "Skipped — tick to print"}
                          data-testid={`barcode-select-${e.product.sku}-${v.size}`}
                        />
                        <span className="font-mono text-xs text-amber-400">{variantCode(e.product.sku, v.size)}</span>
                        <input type="number" min={0} value={v.copies} disabled={!v.selected} onChange={(ev) => setVariantCopies(ei, vi, ev.target.value)} className="w-12 bg-slate-950 border border-slate-800 rounded px-2 py-0.5 text-xs text-slate-200 font-mono text-right disabled:opacity-40" data-testid={`barcode-copies-${e.product.sku}-${v.size}`} />
                        <span className="text-[10px] text-slate-500">copies</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Printable sheet */}
      <div className="pos-print-sheet-wrapper bg-white text-black p-4 rounded" style={{ display: totalLabels ? "block" : "none" }}>
        <div className="flex flex-wrap gap-2 justify-start">
          {printLabels.map((l, i) => <BarcodeLabel key={i} product={l.product} code={l.code} size={l.size} label={l.label} />)}
        </div>
      </div>

      {/* Browse catalogue modal */}
      {browseOpen && (
        <div className="pos-print-hide fixed inset-0 z-50 bg-black/70 grid place-items-center p-3 sm:p-6" onClick={() => setBrowseOpen(false)} data-testid="barcode-browse-modal">
          <div className="bg-slate-900 border border-slate-800 rounded-lg max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 sm:p-5 border-b border-slate-800 flex flex-wrap items-center gap-3">
              <Grid3x3 className="text-amber-500 shrink-0" size={20} />
              <h2 className="text-lg font-semibold text-slate-100">Browse products</h2>
              <button onClick={() => setBrowseOpen(false)} className="text-slate-400 hover:text-slate-100 ml-auto"><X size={18} /></button>
              <div className="relative w-full order-last">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input autoFocus value={browseQuery} onChange={(e) => setBrowseQuery(e.target.value)} placeholder="Filter by name, SKU or category" className="w-full bg-slate-950 border border-slate-800 rounded pl-9 pr-3 py-2 text-sm text-slate-200" data-testid="barcode-browse-filter" />
              </div>
            </div>
            <div className="p-4 overflow-y-auto grid sm:grid-cols-2 gap-2">
              {filteredBrowse.length === 0 && <p className="col-span-full text-center text-slate-500 text-sm py-10">{browseLoaded ? "No products match." : "Loading catalogue…"}</p>}
              {filteredBrowse.map((p) => {
                const added = entries.find((e) => e.product.id === p.id);
                return (
                  <button key={p.id} onClick={() => addProduct(p)} disabled={!!added} className="flex items-center gap-3 p-2.5 bg-slate-950 border border-slate-800 rounded text-left hover:border-amber-500 disabled:opacity-50" data-testid={`barcode-browse-item-${p.sku || p.slug}`}>
                    <img src={p.primary_image} alt="" className="h-12 w-12 object-cover rounded" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-100 truncate">{p.name}</p>
                      <p className="text-xs text-slate-500 font-mono truncate">SKU {p.sku || "—"} · {(p.sizes || []).join(", ") || "—"}</p>
                    </div>
                    <span className={`text-xs ${added ? "text-emerald-400" : "text-amber-400"} flex items-center gap-1`}>{added ? "Added" : <><Plus size={13} /> Add</>}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
